import { trackById } from 'src/app/shared/utils/track-by.util';
import { Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { ControlOrgLevelSegDialogComponent } from 'src/app/modals/control-org-level-seg-dialog/control-org-level-seg-dialog.component';
import { ControlOrganizationLevelSeg } from 'src/app/models/controlOrganizationLevelSeg.model';
import { Control } from 'src/app/models/control.model';
import { ControlOrganizationLevelSegService } from 'src/app/services/controlOrganizationLevelSeg.service';
import { ControlService } from 'src/app/services/control.service';
import { ToastService } from 'src/app/services/toast.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ControlTypeEnum } from 'src/app/enums/control-type.enum';
import { ControlStatusEnum } from 'src/app/enums/control-status.enum';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { ILookup } from 'src/app/shared/form/lookup.model';

interface ControlOrgLevelSegVm extends ControlOrganizationLevelSeg {
    targetStartFormatted: string;
    targetEndFormatted: string;
    implStartFormatted: string;
    implCompleteFormatted: string;
    statusCssClass: string;
}

@Component({
  standalone: false,
    selector: 'app-control-details-popup',
    templateUrl: './control-details-popup.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './control-details-popup.component.scss',
})
export class ControlDetailsPopupComponent implements OnInit, OnDestroy {
    controlDetails: Control;
    controlTypeName = '-';
    displayedColumns: string[] = [];
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    dataSource = new MatTableDataSource<ControlOrgLevelSegVm>();
    isLoading = false;
    totalRecords = 0;
    statusOptions: ILookup[] = [];
    private paginatorSubscription: Subscription;

    constructor(
        @Inject(FLYOUT_DATA) public control: Control,
        private controlService: ControlService,
        private controlOrgLevelSegService: ControlOrganizationLevelSegService,
        private toastService: ToastService,
        protected dialog: MatDialog,
        private dateFormatService: DateFormatService
    ) {
        this.controlDetails = control;
        this.controlTypeName = this.getControlTypeName();
        this.statusOptions = getEnumLookup(ControlStatusEnum);
    }

    ngOnInit(): void {
        this.displayedColumns = [
            nameOf<ControlOrganizationLevelSeg>(x => x.segId),
            nameOf<ControlOrganizationLevelSeg>(x => x.organizationLevelId),
            nameOf<ControlOrganizationLevelSeg>(x => x.ownerName),
            nameOf<ControlOrganizationLevelSeg>(x => x.status),
            nameOf<ControlOrganizationLevelSeg>(x => x.targetImplementationStart),
            nameOf<ControlOrganizationLevelSeg>(x => x.targetImplementationEnd),
            nameOf<ControlOrganizationLevelSeg>(x => x.dateImplementationStart),
            nameOf<ControlOrganizationLevelSeg>(x => x.dateImplementationComplete),
            nameOf<ControlOrganizationLevelSeg>(x => x.notes),
            'actions'
        ];

        if (this.control?.id) {
            this.loadData();
        }
    }


    private setupPaginatorSubscription(): void {
        if (this.paginator && !this.paginatorSubscription) {
            this.paginatorSubscription = this.paginator.page.subscribe(() => {
                this.loadPagedData();
            });
        }
    }

    private toVm(items: ControlOrganizationLevelSeg[]): ControlOrgLevelSegVm[] {
        return items.map(item => ({
            ...item,
            targetStartFormatted: this.formatDate(item.targetImplementationStart),
            targetEndFormatted: this.formatDate(item.targetImplementationEnd),
            implStartFormatted: this.formatDate(item.dateImplementationStart),
            implCompleteFormatted: this.formatDate(item.dateImplementationComplete),
            statusCssClass: this.getStatusCssClass(item.status)
        }));
    }

    loadData(): void {
        this.isLoading = true;
        const filterBy = JSON.stringify({ controlId: this.control.id });
        const pageSize = this.paginator?.pageSize || 10;

        this.controlOrgLevelSegService.get('', '', 'asc', 0, pageSize, filterBy).subscribe({
            next: (response) => {
                this.totalRecords = response.count;
                this.dataSource.data = this.toVm(response.data);
                this.isLoading = false;

                // Set up paginator after view is rendered
                setTimeout(() => {
                    if (this.paginator) {
                        this.paginator.length = this.totalRecords;
                        this.setupPaginatorSubscription();
                    }
                });
            },
            error: (error) => {
                this.toastService.showError(error);
                this.dataSource.data = [];
                this.isLoading = false;
            }
        });
    }

    loadPagedData(): void {
        const filterBy = JSON.stringify({ controlId: this.control.id });
        const { pageIndex = 0, pageSize = 10 } = this.paginator || {};

        this.controlOrgLevelSegService.get('', '', 'asc', pageIndex, pageSize, filterBy).subscribe({
            next: (response) => {
                this.totalRecords = response.count;
                this.dataSource.data = this.toVm(response.data);
                if (this.paginator) {
                    this.paginator.length = this.totalRecords;
                }
            },
            error: (error) => {
                this.toastService.showError(error);
                this.dataSource.data = [];
            }
        });
    }

    formatDate(date: Date | string | null | undefined): string {
        if (!date) return '-';

        const dateValue = date.toString();

        if (/^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
            return this.dateFormatService.formatDateTimeToString(
                this.dateFormatService.parseDateComponents(dateValue),
                this.dateFormatService.userDateFormat
            );
        }

        return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
            dateValue,
            this.dateFormatService.userDateFormat
        );
    }

    getControlTypeName(): string {
        if (this.controlDetails?.controlType === null || this.controlDetails?.controlType === undefined) {
            return '-';
        }
        return ControlTypeEnum[this.controlDetails.controlType] || '-';
    }

    getStatusCssClass(status: ControlStatusEnum): string {
        switch (status) {
            case ControlStatusEnum.Planned:
                return 'status-planned';
            case ControlStatusEnum.Ongoing:
                return 'status-ongoing';
            case ControlStatusEnum.Implemented:
                return 'status-implemented';
            case ControlStatusEnum.Retired:
                return 'status-retired';
            default:
                return '';
        }
    }

    onStatusChange(element: ControlOrgLevelSegVm, newStatus: ControlStatusEnum): void {
        const originalStatus = element.status;
        const originalStatusCssClass = element.statusCssClass;
        element.status = newStatus;
        element.statusCssClass = this.getStatusCssClass(newStatus);

        const payload: any = { status: newStatus };
        this.controlOrgLevelSegService.patch(element.id, payload).subscribe({
            next: () => {
                this.toastService.showSuccess('Status updated successfully', true);
            },
            error: (error) => {
                element.status = originalStatus;
                element.statusCssClass = originalStatusCssClass;
                this.toastService.showError(error);
            }
        });
    }

    openControlOrgLevelSegDialog(entityId?: string): void {
        const data: IFormDialogData & { controlId?: string } = {
            id: entityId ?? '',
            controlId: this.control.id
        };

        this.handleDialog(
            this.dialog.open(ControlOrgLevelSegDialogComponent, {
                data,
                width: '760px',
                disableClose: true
            })
        );
    }

    openControlOrgLevelSegDeleteDialog(id?: number): void {
        this.handleDialog(
            this.dialog.open(DeleteDialog, {
                data: {
                    id,
                    formDeletes: true,
                    service: this.controlOrgLevelSegService,
                    title: 'Control SEG'
                },
                width: '440px',
                disableClose: true
            })
        );
    }

    private handleDialog(dialogRef: MatDialogRef<any>): void {
        dialogRef.afterClosed().subscribe((result: any) => {
            if (result) this.loadData();
        });
    }

    ngOnDestroy(): void {
        this.paginatorSubscription?.unsubscribe();
    }

    public trackById = trackById;
}